import {
	Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, SimpleChange,
	ViewChildren, ViewChild, AfterViewInit, ElementRef, QueryList, ChangeDetectionStrategy,
	ChangeDetectorRef, OnInit, OnDestroy
} from '@angular/core';
import { MessageService } from 'base';
import { DiffType, DiffSpan, DiffSpans, DiffResult } from 'DiscoveryEngine.TS/CommandLine';


class DiffLine {
	public lineNumber: number;
	public text: string;
	public textStyle: string;
}


@Component({
	selector: 'diff-file',
	templateUrl: './diff-file.component.html',
	styleUrls: ['./diff-file.component.scss']
})
export class DiffFileComponent implements OnInit, OnChanges, OnDestroy {

	@Input()
	public name: string;

	@Input()
	public diffTextStyle: string;

	@Input()
	public matchTextStyle: string;

	@Input()
	public skipTextStyle: string;

	@Input()
	public diffSpans: DiffSpans;

	@Input()
	public topCoord: number;
	@Output()
	public topCoordChange: EventEmitter<number>;

	@Input()
	public leftCoord: number;
	@Output()
	public leftCoordChange: EventEmitter<number>;

	@Input()
	public curDiffIndex: number;
	@Output()
	public curDiffIndexChange: EventEmitter<number>;

	@Input()
	public height: number;

	@Input()
	public width: number;

	@ViewChild('contentDiv', { static: true })
	public contentDiv: ElementRef<HTMLDivElement>;

	@ViewChild('emptyDiv', { static: true })
	public emptyDiv: ElementRef<HTMLDivElement>;

	@ViewChildren('row')
	public rows: QueryList<ElementRef<HTMLTableRowElement>>;

	public lines: DiffLine[];
	public ignoreScrollEvents: boolean;

	private diffLineMap: number[];

	constructor(private messageService: MessageService,
		private changeDetector: ChangeDetectorRef) {

		this.diffTextStyle = "";
		this.matchTextStyle = "";
		this.skipTextStyle = "";
		this.diffSpans = new DiffSpans();
		this.topCoord = 0;
		this.topCoordChange = new EventEmitter<number>();
		this.leftCoord = 0;
		this.leftCoordChange = new EventEmitter<number>();
		this.curDiffIndex = 0;
		this.curDiffIndexChange = new EventEmitter<number>();
		this.height = 0;
		this.width = 0;
		this.lines = [];
		this.diffLineMap = [];
		this.ignoreScrollEvents = false;
	}

	public ngOnInit(): void {

	}

	public ngAfterViewInit(): void {

		this.calcCurDiffIndex();

		this.rows.changes.subscribe(() => {
			this.calcCurDiffIndex();
		});
	}

	private displayRows(): void {
		let rowsArray: ElementRef<HTMLTableRowElement>[] = this.rows.toArray();
		rowsArray.forEach((row: ElementRef<HTMLTableRowElement>) => {
			console.log("offsetTop: " + row.nativeElement.offsetTop);
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (changes["diffSpans"]) {
			this.rebuildLines();
		}

		if (changes["topCoord"]) {
			let tmpTopCoord = this.topCoord;
			//console.log(`${this.name}: Change to topCoord=${this.topCoord}`);
			this.contentDiv.nativeElement.scrollTop = this.topCoord;
		}

		if (changes["leftCoord"]) {
			//console.log(`${this.name}: Change to leftCoord=${this.leftCoord}`);
			this.contentDiv.nativeElement.scrollLeft = this.leftCoord;
		}

		if (changes["curDiffIndex"]) {
			// ????
		}

		if (changes["height"]) {
			//console.log(`${this.name}: Change to height=${this.height}`);
			this.contentDiv.nativeElement.style.height = this.height + 'px';
			this.emptyDiv.nativeElement.style.height = this.height + 'px';
		}

		if (changes["width"]) {
			//console.log(`${this.name}: Change to width=${this.width}`);
			this.contentDiv.nativeElement.style.width = this.width + 'px';
		}

		/* This is best done in ngAfterViewInit where the offsetTop attributes have already been set.
		 * 
		////The table row element offsetTop attributes are all zero at this point.
		////Therefore, calcDurDiffIndex  must be called after a delay to allow the
		////table row elements to be positioned on the screen before calculating curDiffIndex
		//setTimeout(() => this.calcCurDiffIndex(), 10);
		*/
	}

	public ngOnDestroy(): void {

	}

	private rebuildLines(): void {

		this.lines = [];
		this.diffLineMap = [];

		if (this.diffSpans) {

			let globalLineNumber = 0;

			this.diffSpans.spans.ForEach((diff: DiffSpan) => {

				if (diff.type != DiffType.Match) {
					this.diffLineMap.push(this.lines.length);
				}

				let lines: string[] = this.diffSpans.getDiffLines(diff);

				for (let i = 0; i < lines.length; ++i) {

					++globalLineNumber;

					let diffLine = new DiffLine();
					diffLine.lineNumber = globalLineNumber;

					if (lines[i]) {
						diffLine.text = lines[i];
					}
					else {
						diffLine.text = "";
					}

					switch (diff.type) {
						case DiffType.Diff:
							diffLine.textStyle = this.diffTextStyle;
							break;
						case DiffType.Match:
							diffLine.textStyle = this.matchTextStyle;
							break;
						case DiffType.Skip:
							diffLine.textStyle = this.skipTextStyle;
							break;
					}

					this.lines.push(diffLine);
				}
			});
		}
	}

	public get haveDiffs(): boolean {
		return (!!this.diffLineMap && this.diffLineMap.length > 0 &&
			!!this.rows && this.rows.length > 0);
	}

	public get enablePrevDiff(): boolean {
		return (this.haveDiffs && this.curDiffIndex > 1);
	}

	public prevDiff(): void {

		if (this.enablePrevDiff) {
			--this.curDiffIndex;

			// In the UI diff indexes are 1-based.
			let diffRowIndex = this.diffLineMap[this.curDiffIndex - 1];

			let rowsArray: ElementRef<HTMLTableRowElement>[] = this.rows.toArray();
			let diffRow: ElementRef<HTMLTableRowElement> = rowsArray[diffRowIndex];

			this.topCoord = diffRow.nativeElement.offsetTop;
			this.contentDiv.nativeElement.scrollTop = this.topCoord;

			this.curDiffIndexChange.emit(this.curDiffIndex);
			this.topCoordChange.emit(this.topCoord);
		}
		else {
			console.log("Skipping prevDiff");
		}
	}

	public get enableNextDiff(): boolean {
		return (this.haveDiffs && (this.curDiffIndex < this.diffLineMap.length));
	}

	public nextDiff(): void {

		if (this.enableNextDiff) {

			++this.curDiffIndex;

			// In the UI diff indexes are 1-based.
			let diffRowIndex = this.diffLineMap[this.curDiffIndex - 1];

			let rowsArray: ElementRef<HTMLTableRowElement>[] = this.rows.toArray();
			let diffRow: ElementRef<HTMLTableRowElement> = rowsArray[diffRowIndex];

			this.topCoord = diffRow.nativeElement.offsetTop;
			this.contentDiv.nativeElement.scrollTop = this.topCoord;

			this.curDiffIndexChange.emit(this.curDiffIndex);
			this.topCoordChange.emit(this.topCoord);
		}
		else {
			console.log("Skipping nextDiff");
		}
	}

	public onScroll(scrollLeft: number, scrollTop: number): void {

		if (this.ignoreScrollEvents) {
			//console.log(`${this.name}: Skipping onScroll(${scrollLeft}, ${scrollTop})`);
			return;
		}

		//console.log(`${this.name}: Doing onScroll(${scrollLeft}, ${scrollTop})`);

		let leftCoordChanged = false;
		if (scrollLeft != this.leftCoord) {
			this.leftCoord = scrollLeft;
			leftCoordChanged = true;
		}

		let topCoordChanged = false;
		if (scrollTop != this.topCoord) {
			this.topCoord = scrollTop;
			topCoordChanged = true;
		}

		this.calcCurDiffIndex();

		if (leftCoordChanged) {
			this.leftCoordChange.emit(this.leftCoord);
		}

		if (topCoordChanged) {
			this.topCoordChange.emit(this.topCoord);
		}
	}

	private calcCurDiffIndex(): void {

		let oldCurDiffIndex = this.curDiffIndex;

		this.curDiffIndex = 0;

		if (this.haveDiffs) {
			let rowsArray: ElementRef<HTMLTableRowElement>[] = this.rows.toArray();
			for (let i = 0; i < this.diffLineMap.length; ++i) {
				let diffRowIndex: number = this.diffLineMap[i];
				let diffRow: ElementRef<HTMLTableRowElement> = rowsArray[diffRowIndex];
				if (this.topCoord >= diffRow.nativeElement.offsetTop) {
					++this.curDiffIndex;
				}
				else {
					break;
				}
			}
		}

		if ((oldCurDiffIndex != this.curDiffIndex) && (this.name === "left")) {
			this.curDiffIndexChange.emit(this.curDiffIndex);
		}
	}

}
